import React, { useEffect, useState, Suspense } from "react";
import { useIsRestoring } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { App as CapacitorApp } from '@capacitor/app';
import Sidebar from "./components/Sidebar";
import SignupPage from "./auth/SignupPage";
import LoginPage from "./auth/LoginPage";
import ProtectedRoute from "./ProtectedRoute";
import NavBar from "./components/NavBar";
import NoArtistsPage from "./NoArtistsPage";
import ErrorPage from "./Error";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import LoadingPage from "./components/LoadingPage";
import GoogleMapsProvider from "./constants/GoogleMapsProvider";
import { useArtists, useActiveArtists } from "./contexts/ArtistContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import BottomMenu from "./components/BottomMenu";
import NotificationManager from "./components/NotificationManager";
import PushNotificationHandler from "./components/PushNotificationHandler";
import { LiveActivityProvider } from "./contexts/LiveActivityContext";
import { RemoteConfigProvider } from "./contexts/RemoteConfigContext";
import GlobalLiveActivityManager from "./components/GlobalLiveActivityManager";
import { QuickCreateProvider } from "./contexts/QuickCreateContext";
import useOtaUpdate from "./hooks/useOtaUpdate";
import useSwipeBack from "./hooks/useSwipeBack";
import useChangeSignal from "./hooks/useChangeSignal";
import UpdateBanner from "./components/UpdateBanner";

// Lazy load heavy components for better performance
const CalendarPage = React.lazy(() => import("./screens/CalendarPage"));
const AddArtistPage = React.lazy(() => import("./screens/ArtistCreatePage"));
const ListArtistsPage = React.lazy(() => import("./screens/ArtistListPage"));
const EventPage = React.lazy(() => import("./screens/Event/EventPage"));
const ArtistPage = React.lazy(() => import("./screens/ArtistPage"));
const CrewMemberPage = React.lazy(() => import("./screens/CrewMemberPage"));
const EventDashboard = React.lazy(() => import("./screens/Event/EventDashboard"));
const EventSchedule = React.lazy(() => import("./screens/Event/EventSchedule"));
const EventInfo = React.lazy(() => import("./screens/Event/EventInfo"));
const AdvancingPage = React.lazy(() => import("./screens/Event/AdvancingPage"));
const HotelPage = React.lazy(() => import("./screens/Event/Hotel"));
const TicketSales = React.lazy(() => import("./screens/Event/TicketSales"));
const EventFiles = React.lazy(() => import("./screens/Event/EventFiles"));
const EventCrewManager = React.lazy(() => import("./screens/Event/EventCrewManager"));
const TravelOverview = React.lazy(() => import("./screens/Event/TravelOverview"));
const EventMap = React.lazy(() => import("./screens/Event/EventMap"));
const GuestList = React.lazy(() => import("./screens/Event/GuestList"));
const EventTypeListScreen = React.lazy(() => import("./screens/EventTypeListScreen"));
const ActivityPage = React.lazy(() => import("./screens/Activity/ActivityPage"));
const TourManagement = React.lazy(() => import("./screens/Tours/TourManagement"));
const TourEvents = React.lazy(() => import('./screens/Tours/TourEvents'));
const TourExpenses = React.lazy(() => import('./screens/Tours/TourExpenses'));
const TourStats = React.lazy(() => import('./screens/Tours/TourStats'));
const TourCrewManager = React.lazy(() => import('./screens/Tours/TourCrewManager'));
const NightlinerTab = React.lazy(() => import('./screens/Tours/NightlinerTab'));
const TourAccommodation = React.lazy(() => import('./screens/Tours/TourAccommodation'));
const TourLogisticsMatrix = React.lazy(() => import('./screens/Tours/TourLogisticsMatrix'));
const InboxPage = React.lazy(() => import("./screens/InboxPage"));
const DataMigration = React.lazy(() => import("./pages/DataMigration"));
const MessagingPage = React.lazy(() => import("./screens/MessagingPage"));
const MerchandiseDashboard = React.lazy(() => import("./screens/Merchandise/MerchandiseDashboard"));
const ProfilePage = React.lazy(() => import("./screens/ProfilePage"));
const PrivacyPolicy = React.lazy(() => import("./screens/Legal/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./screens/Legal/TermsOfService"));
const SupportPage = React.lazy(() => import("./screens/Legal/SupportPage"));
const MarketingPage = React.lazy(() => import("./screens/Legal/MarketingPage"));
const LandingPage = React.lazy(() => import("./screens/LandingPage"));
const EventMerchManager = React.lazy(() => import("./screens/Merchandise/EventMerchManager"));
const InviteClaimPage = React.lazy(() => import("./screens/InviteClaimPage"));
const OAuthAuthorizePage = React.lazy(() => import("./screens/OAuthAuthorizePage"));
const PublicAdvancePage = React.lazy(() => import("./screens/PublicAdvancePage"));
const PrintEventsPage = React.lazy(() => import("./screens/Print/PrintEventsPage"));
const PublicSharedEventsPage = React.lazy(() => import("./screens/Public/PublicSharedEventsPage"));

const OnboardingDialog = React.lazy(() => import("./components/OnboardingDialog"));
const MyTasksPage = React.lazy(() => import("./screens/MyTasksPage"));

// Lazy loaded layouts
const ArtistLayout = React.lazy(() => import("./components/layouts/ArtistLayout"));
// React.lazy requires default exports, so we bridge named exports:
const TourLayout = React.lazy(() => import("./components/layouts/NestedLayouts").then(module => ({ default: module.TourLayout })));
const EventLayout = React.lazy(() => import("./components/layouts/NestedLayouts").then(module => ({ default: module.EventLayout })));

console.time('App First Render');
let firstRender = true;

function AppContent() {
    const navigate = useNavigate();

    useEffect(() => {
        CapacitorApp.addListener('appUrlOpen', (event) => {
            try {
                const url = new URL(event.url);
                if (url.pathname.startsWith('/invite/')) {
                    navigate(url.pathname + url.search);
                }
            } catch (e) {
                console.error("Failed to parse appUrlOpen", e);
            }
        });

        return () => {
            CapacitorApp.removeAllListeners();
        };
    }, [navigate]);

    if (firstRender) {
        console.timeEnd('App First Render');
        firstRender = false;
    }

    // Initialize OTA updater (no-op on web)
    useOtaUpdate();

    // iOS-style swipe from left edge to go back
    useSwipeBack();

    // Determine visibility of global layout elements is now handled by the layouts themselves
    return (
        <div className="app-container">
            <GoogleMapsProvider>
                <NotificationManager />
                <PushNotificationHandler />
                <UpdateBanner />
                <AppRoutes />
            </GoogleMapsProvider>
        </div>
    );
}

function AppRoutes() {
    const isRestoring = useIsRestoring();
    const { user, loading, hasError, invites } = useAuth();
    const { data: activeArtists, isLoading: artistsLoading, isError: artistsError, error: artistsFetchError } = useActiveArtists();

    // Listen for changes made by other users on every page, not just the calendar
    useChangeSignal((activeArtists || []).map(a => a.id));

    // 1. Critical: Wait for the local database to "wake up" (isRestoring) 
    // and for the user to be authenticated (loading).
    if (loading || isRestoring) {
        return <LoadingPage />;
    }

    return (
        <>
            <Suspense fallback={null}>
                <OnboardingDialog />
            </Suspense>
            {invites && invites.length > 0 ? (
                <div className="alert alert-warning text-center">
                    <a href="/artists">You have pending invites.</a>
                </div>
            ) : null}
            <ErrorBoundary>
                <Suspense fallback={<LoadingPage />}>
                    <Routes>
                        <Route path="/signup" element={<SignupPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/support" element={<SupportPage />} />
                        <Route path="/supprort" element={<SupportPage />} />
                        <Route path="/marketing" element={<MarketingPage />} />
                        <Route path="/invite/:inviteId" element={<InviteClaimPage />} />
                        <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
                        <Route path="/public/advance/:eventId" element={<PublicAdvancePage />} />
                        <Route path="/print/events" element={<ProtectedRoute user={user}><PrintEventsPage /></ProtectedRoute>} />
                        <Route path="/public/share/:token" element={<PublicSharedEventsPage />} />


                        <Route
                            path="/"
                            element={
                                user ? (
                                    <ArtistLayout />
                                ) : (
                                    <LandingPage />
                                )
                            }
                        >
                            <Route index element={
                                hasError != null ? <ErrorPage error={hasError} /> :
                                    (activeArtists && activeArtists.length > 0) ? <CalendarPage /> :
                                        (artistsError && !activeArtists && navigator.onLine) ? <ErrorPage error={artistsFetchError} /> :
                                            (artistsLoading && !activeArtists) ? <LoadingPage /> : <NoArtistsPage />
                            } />
                        </Route>

                        <Route path="/add-artist" element={<ProtectedRoute user={user}><ArtistLayout /></ProtectedRoute>}>
                            <Route index element={<AddArtistPage />} />
                        </Route>
                        <Route path="/artists" element={<ProtectedRoute user={user}><ArtistLayout /></ProtectedRoute>}>
                            <Route index element={<ListArtistsPage />} />
                        </Route>

                        {/* ARTIST CONTEXT ROUTES */}
                        <Route path="/artist/:id" element={<ProtectedRoute user={user}><ArtistLayout /></ProtectedRoute>}>
                            <Route index element={<ArtistPage />} /> {/* Dashboard/Overview */}
                            <Route path="calendar" element={<ArtistPage />} /> {/* Reusing artist page as calendar for now */}
                            <Route path="crew" element={<ArtistPage />} /> {/* TODO: Extract specific components */}
                            <Route path="crew/:userId" element={<CrewMemberPage />} />
                            <Route path="merchandise" element={<MerchandiseDashboard />} />
                            <Route path="tours" element={<TourManagement />} />
                        </Route>

                        <Route path="/messages" element={
                            <ProtectedRoute user={user}>
                                <ArtistLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<MessagingPage />} />
                        </Route>

                        <Route path="/tasks" element={
                            <ProtectedRoute user={user}>
                                <ArtistLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<MyTasksPage />} />
                        </Route>

                        <Route path="/inbox" element={
                            <ProtectedRoute user={user}>
                                <ArtistLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<InboxPage />} />
                        </Route>

                        <Route path="/profile" element={<ProtectedRoute user={user}><ArtistLayout /></ProtectedRoute>}>
                            <Route index element={<ProfilePage />} />
                        </Route>

                        {/* TOUR CONTEXT ROUTES */}
                        <Route path="/artist/:id/tours/:tourId" element={<ProtectedRoute user={user}><TourLayout /></ProtectedRoute>}>
                            <Route index element={<TourEvents />} />
                            <Route path="expenses" element={<TourExpenses />} />
                            <Route path="stats" element={<TourStats />} />
                            <Route path="venues" element={<div>Venues Placeholder</div>} />
                            <Route path="crew" element={<TourCrewManager />} />
                            <Route path="nightliner" element={<NightlinerTab />} />
                            <Route path="accommodation" element={<TourAccommodation />} />
                            <Route path="matrix" element={<TourLogisticsMatrix />} />
                        </Route>

                        {/* EVENT CONTEXT ROUTES */}
                        <Route path="/event/:eventId" element={<ProtectedRoute user={user}><EventLayout /></ProtectedRoute>}>
                            <Route index element={<EventInfo />} />
                            <Route path="itinerary" element={<EventSchedule />} />
                            <Route path="dashboard" element={<EventDashboard />} />
                            <Route path="info" element={<EventInfo />} />
                            <Route path="advancing" element={<AdvancingPage />} />
                            <Route path="hotels" element={<HotelPage />} />
                            <Route path="tickets" element={<TicketSales />} />
                            <Route path="guestlist" element={<GuestList />} />
                            <Route path="files" element={<EventFiles />} />
                            <Route path="map" element={<EventMap />} />
                            <Route path="crew" element={<EventCrewManager />} />
                            <Route path="travel" element={<TravelOverview />} />
                            <Route path="merch" element={<EventMerchManager />} />
                        </Route>

                        <Route path="/activity/:activityId" element={
                            <ProtectedRoute user={user}>
                                <ArtistLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<ActivityPage />} />
                        </Route>

                        <Route path="/eventtypes" element={
                            <ProtectedRoute user={user}>
                                <ArtistLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<EventTypeListScreen />} />
                        </Route>
                        <Route path="/migration" element={
                            <ProtectedRoute user={user}>
                                <ArtistLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<DataMigration />} />
                        </Route>
                    </Routes>
                </Suspense>
            </ErrorBoundary>
        </>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <ThemeProvider>
                <RemoteConfigProvider>
                    <AuthProvider>
                        <ImpersonationProvider>
                            <LiveActivityProvider>
                                <GlobalLiveActivityManager />
                                <QuickCreateProvider>
                                    <Router>
                                        <AppContent />
                                    </Router>
                                </QuickCreateProvider>
                            </LiveActivityProvider>
                        </ImpersonationProvider>
                    </AuthProvider>
                </RemoteConfigProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}
export default App; // Trigger deployment